import React, { JSX, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Edit,
  Trash2,
  Eye,
  Ban,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  RotateCcw,
} from "lucide-react";

export type ActionType =
  | "approve"
  | "reject"
  | "retry"
  | "pending"
  | "edit"
  | "delete"
  | "view"
  | "block"
  | "like"
  | "dislike";

export interface Action {
  type: ActionType;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ActionMenuProps {
  actions: Action[];
  className?: string;
  iconSize?: "sm" | "md" | "lg";
  variant?: "outline" | "filled" | "ghost";
  dropdown?: boolean;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  actions = [],
  className = "",
  iconSize = "md",
  variant = "outline",
  dropdown = false,
}) => {
  const [open, setOpen] = useState(false);

  const getIcon = (type: ActionType, size: number) => {
    const icons: Record<ActionType, JSX.Element> = {
      approve: <CheckCircle size={size} />,
      reject: <XCircle size={size} />,
      retry: <RotateCcw size={size} />,
      pending: <Clock size={size} />,
      edit: <Edit size={size} />,
      delete: <Trash2 size={size} />,
      view: <Eye size={size} />,
      block: <Ban size={size} />,
      like: <ThumbsUp size={size} />,
      dislike: <ThumbsDown size={size} />,
    };
    return icons[type];
  };

  const getBgColor = (type: ActionType, disabled?: boolean) => {
    if (disabled)
      return "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200";

    const colors: Record<ActionType, string> = {
      approve: "bg-green-100 text-green-700 hover:bg-green-200",
      reject: "bg-red-100 text-red-700 hover:bg-red-200",
      retry: "bg-amber-100 text-amber-700 hover:bg-amber-200",
      pending: "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
      edit: "bg-blue-100 text-blue-700 hover:bg-blue-200",
      delete: "bg-red-100 text-red-700 hover:bg-red-200",
      view: "bg-gray-100 text-gray-700 hover:bg-gray-200",
      block: "bg-orange-100 text-orange-700 hover:bg-orange-200",
      like: "bg-green-100 text-green-700 hover:bg-green-200",
      dislike: "bg-red-100 text-red-700 hover:bg-red-200",
    };
    return colors[type];
  };

  const sizeMap = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  const btnSizeMap = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };

  const size = sizeMap[iconSize];
  const btnSize = btnSizeMap[iconSize];

  if (dropdown) {
    return (
      <div className="relative inline-block text-left">
        <button
          onClick={() => setOpen(!open)}
          className="p-2 rounded-full hover:bg-gray-200 transition"
          aria-label="Actions"
        >
          <MoreVertical className="w-6 h-6" />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50">
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (!action.disabled) action.onClick();
                  setOpen(false);
                }}
                disabled={action.disabled}
                className={`flex items-center gap-2 w-full px-4 py-2 text-sm rounded transition ${
                  action.disabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
              >
                {getIcon(action.type, size)}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-start gap-2 ${className}`}>
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={action.disabled ? undefined : action.onClick}
          disabled={action.disabled}
          title={action.label}
          aria-label={action.label}
          className={`
            ${btnSize} flex items-center justify-center rounded-full
            ${getBgColor(action.type, action.disabled)}
            transition-transform duration-200
            ${
              !action.disabled
                ? "hover:scale-110 active:scale-95 cursor-pointer"
                : "cursor-not-allowed opacity-70"
            }
          `}
        >
          {getIcon(action.type, size)}
        </button>
      ))}
    </div>
  );
};

export default ActionMenu;
